import { env } from "cloudflare:workers";
import { createServerFn } from "@tanstack/react-start";
import { deliverForm, isFormSource } from "./form-delivery";
import {
	type ConsultFormFieldErrors,
	type ConsultFormInput,
	isSpam,
	validateConsultInput,
} from "./leadsigma";

export type SubmitConsultResult =
	| { status: "ok" }
	| { status: "partial" }
	| { status: "invalid"; errors: ConsultFormFieldErrors }
	| { status: "error" };

function readString(data: Record<string, unknown>, key: string): string {
	const value = data[key];
	return typeof value === "string" ? value : "";
}

export function parseFormInput(data: unknown): ConsultFormInput {
	if (typeof data !== "object" || data === null) {
		throw new Error("Expected a form submission object");
	}
	const record = data as Record<string, unknown>;
	return {
		// Keep one extra character so validation rejects oversized values
		// instead of silently truncating them and reporting success.
		fullName: readString(record, "fullName").slice(0, 201),
		email: readString(record, "email").slice(0, 201),
		phone: readString(record, "phone").slice(0, 41),
		patientName: readString(record, "patientName").slice(0, 201),
		message: readString(record, "message").slice(0, 2001),
		source: readString(record, "source").slice(0, 50),
		locale: readString(record, "locale") === "es" ? "es" : "en",
		website: readString(record, "website").trim().slice(0, 200),
	};
}

export const submitConsultLead = createServerFn({ method: "POST" })
	.inputValidator(parseFormInput)
	.handler(async ({ data }): Promise<SubmitConsultResult> => {
		if (isSpam(data)) return { status: "ok" };
		const errors = validateConsultInput(data);
		if (!isFormSource(data.source)) errors.source = "invalid";
		if (Object.keys(errors).length > 0) {
			return { status: "invalid", errors };
		}

		if (!isFormSource(data.source)) return { status: "error" };
		const settings = env as unknown as Record<string, unknown>;
		return deliverForm(
			{ ...data, source: data.source },
			settings,
			settings.FORM_EMAIL as SendEmail | undefined,
		);
	});
